// Gebetszeiten (Baustein „prayer"): Berechnung, Zeitpläne, aktuelles/nächstes Gebet, Hijri-Datum und Texte.
// Ohne DOM, damit Player, Manager und Server dieselbe Logik verwenden. Alles läuft offline im Player:
// Berechnung mit adhan-js (MIT, https://github.com/batoulapps/adhan-js), Zeitpläne kommen über das Manifest.
// Jeder Tag wird für sich in der Zeitzone des Standorts ausgewertet; Mitternacht und Zeitumstellung brauchen
// daher keine Sonderbehandlung (Zeitpunkte sind UTC-Millisekunden, Anzeige als Ortszeit).
import { CalculationMethod, CalculationParameters, Coordinates, HighLatitudeRule, Madhab, PolarCircleResolution, PrayerTimes } from 'adhan';
import { tzOffsetMs } from './analog.js';
import { addDays } from './data.js';
export const PRAYER_KEYS = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
/** Gebete mit Iqama (ohne Sonnenaufgang). */
export const IQAMA_KEYS = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];
/**
 * Berechnungsmethoden. Winkel und Korrekturen wie in adhan-js; „uoif" (Frankreich, 12°/12°) wie Methode 12 von
 * api.aladhan.com; „custom" mit eigenen Winkeln (z. B. Vorgaben der eigenen Gemeinschaft).
 */
export const PRAYER_METHODS = [
    'diyanet', 'mwl', 'isna', 'umm_al_qura', 'egypt', 'karachi', 'dubai', 'moonsighting', 'kuwait', 'qatar', 'singapore', 'tehran', 'uoif', 'custom',
];
export const HIGH_LAT_RULES = ['auto', 'middle', 'seventh', 'angle', 'none'];
export const PRAYER_LANGS = ['de', 'en', 'tr', 'bs', 'ar'];
export const PRAYER_VIEWS = ['table', 'column', 'bar'];
export const PRAYER_SOURCES = ['calc', 'table', 'provider'];
/** Iqama-Angabe: "" = keine, "+10" = 10 Minuten nach dem Gebetsbeginn, "13:30" = feste Uhrzeit. */
export const IQAMA_RE = /^(\+\d{1,3}|([01]\d|2[0-3]):[0-5]\d)?$/;
/** Jumu'ah-Zeiten, mit Komma getrennt, z. B. "13:15, 14:30". */
export const JUMUAH_RE = /^\s*(([01]?\d|2[0-3]):[0-5]\d\s*(,\s*([01]?\d|2[0-3]):[0-5]\d\s*){0,3})?$/;
// ---------- Kalendertage in einer Zeitzone ----------
const ymdFormatters = new Map();
/** Kalendertag "YYYY-MM-DD" eines Zeitpunkts in der Zeitzone (ohne: Systemzeitzone). */
export function ymdAt(at, timeZone) {
    const key = timeZone ?? '';
    let f = ymdFormatters.get(key);
    if (!f) {
        f = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', ...(timeZone ? { timeZone } : {}) });
        ymdFormatters.set(key, f);
    }
    const p = f.formatToParts(new Date(at));
    const v = (t) => p.find((x) => x.type === t)?.value ?? '';
    return `${v('year')}-${v('month')}-${v('day')}`;
}
const parseYmd = (ymd) => ymd.split('-').map(Number);
/** Wochentag 0 = Sonntag … 5 = Freitag. */
export function weekdayOf(ymd) {
    const [y, m, d] = parseYmd(ymd);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
/** Ortszeit "HH:mm" an einem Kalendertag → Zeitpunkt (UTC-ms), auch an Tagen mit Zeitumstellung. */
export function instantOf(ymd, hhmm, timeZone) {
    const [y, m, d] = parseYmd(ymd);
    const [hh = 0, mm = 0] = hhmm.split(':').map(Number);
    const wall = Date.UTC(y, m - 1, d, hh, mm);
    // Zweimal annähern: der Abstand zu UTC hängt vom gesuchten Zeitpunkt selbst ab
    let at = wall - tzOffsetMs(wall, timeZone);
    at = wall - tzOffsetMs(at, timeZone);
    return at;
}
/** Zeitpunkt → "HH:mm" in der Zeitzone. */
export function hhmmAt(at, timeZone) {
    const d = new Date(at + tzOffsetMs(at, timeZone));
    return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}
// ---------- Berechnung ----------
function baseParams(spec) {
    switch (spec.method ?? 'mwl') {
        case 'diyanet': return CalculationMethod.Turkey();
        case 'isna': return CalculationMethod.NorthAmerica();
        case 'umm_al_qura': return CalculationMethod.UmmAlQura();
        case 'egypt': return CalculationMethod.Egyptian();
        case 'karachi': return CalculationMethod.Karachi();
        case 'dubai': return CalculationMethod.Dubai();
        case 'moonsighting': return CalculationMethod.MoonsightingCommittee();
        case 'kuwait': return CalculationMethod.Kuwait();
        case 'qatar': return CalculationMethod.Qatar();
        case 'singapore': return CalculationMethod.Singapore();
        case 'tehran': return CalculationMethod.Tehran();
        case 'uoif': return new CalculationParameters('Other', 12, 12);
        case 'custom': {
            const p = new CalculationParameters('Other', clampAngle(spec.fajrAngle, 18), clampAngle(spec.ishaAngle, 17), Math.max(0, Math.round(Number(spec.ishaInterval) || 0)));
            return p;
        }
        default: return CalculationMethod.MuslimWorldLeague();
    }
}
const clampAngle = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 && n <= 30 ? n : fallback;
};
/** Berechnungsparameter des Bausteins (Methode, Asr, hohe Breiten, Minutenkorrekturen). */
export function calcParams(spec, coords) {
    const p = baseParams(spec);
    p.madhab = spec.asr === 'hanafi' ? Madhab.Hanafi : Madhab.Shafi;
    const rule = spec.highLat ?? 'auto';
    if (rule === 'middle')
        p.highLatitudeRule = HighLatitudeRule.MiddleOfTheNight;
    else if (rule === 'seventh')
        p.highLatitudeRule = HighLatitudeRule.SeventhOfTheNight;
    else if (rule === 'angle')
        p.highLatitudeRule = HighLatitudeRule.TwilightAngle;
    else if (rule === 'auto' && coords)
        p.highLatitudeRule = HighLatitudeRule.recommended(new Coordinates(coords.lat, coords.lon));
    // „none": Regel der Methode (adhan: Mitte der Nacht nur, wenn der Winkel nicht erreicht wird)
    // Polarkreis: Zeiten des nächstgelegenen Tages mit gültigen Zeiten statt „ungültig"
    p.polarCircleResolution = PolarCircleResolution.AqrabYaum;
    const adj = spec.adjust ?? {};
    for (const k of PRAYER_KEYS) {
        const v = Math.round(Number(adj[k]) || 0);
        p.adjustments[k] = Math.max(-60, Math.min(60, v));
    }
    return p;
}
/** Hijri-Monat (1–12) nach Umm al-Qura für einen Kalendertag. */
export function hijriMonth(ymd, adjustDays = 0) {
    const [y, m, d] = parseYmd(ymd);
    const parts = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', { month: 'numeric', timeZone: 'UTC' })
        .formatToParts(new Date(Date.UTC(y, m - 1, d + adjustDays)));
    return Number(parts.find((p) => p.type === 'month')?.value ?? 0);
}
/**
 * Berechnete Zeiten eines Kalendertags (sechs Zeitpunkte, auf die Minute gerundet). null ohne Koordinaten.
 * Umm al-Qura: im Ramadan Isha 120 statt 90 Minuten nach Maghrib (Vorgabe der Methode).
 */
export function calcDay(spec, ymd, hijriAdjust = 0) {
    if (spec.lat == null || spec.lon == null || !Number.isFinite(spec.lat) || !Number.isFinite(spec.lon))
        return null;
    const coords = { lat: spec.lat, lon: spec.lon };
    const p = calcParams(spec, coords);
    if ((spec.method ?? 'mwl') === 'umm_al_qura' && hijriMonth(ymd, hijriAdjust) === 9)
        p.ishaInterval = 120;
    const [y, m, d] = parseYmd(ymd);
    // adhan liest Jahr/Monat/Tag aus dem lokalen Date – das ist hier nur der Kalendertag, keine Uhrzeit.
    const t = new PrayerTimes(new Coordinates(coords.lat, coords.lon), new Date(y, m - 1, d), p);
    const out = PRAYER_KEYS.map((k) => t[k].getTime());
    return out.every((x) => Number.isFinite(x)) ? out : null;
}
/** Schlüssel des Zeitplans im Manifest, auf den der Baustein verweist (oder null bei Berechnung). */
export function tableRef(spec) {
    if (spec.source === 'table' && spec.table)
        return `t:${spec.table}`;
    if (spec.source === 'provider' && spec.provider && spec.location)
        return `${spec.provider}:${spec.location}`;
    return null;
}
/**
 * Zeiten eines Kalendertags. Zeitplan zuerst; fehlt der Tag (z. B. Jahreswechsel offline) und sind Koordinaten
 * gesetzt, wird berechnet. Zeiten, die vor der vorherigen liegen (Isha nach Mitternacht), zählen zum Folgetag.
 */
export function dayTimes(spec, ymd, timeZone, tables, hijriAdjust = 0) {
    const ref = tableRef(spec);
    let at = null;
    let fixedIqama = [];
    let source = 'calc';
    const table = ref ? tables?.get(ref) : undefined;
    const row = table ? findDay(table, ymd) : undefined;
    if (row && row.times.length === 6) {
        at = [];
        let prev = -Infinity;
        for (const t of row.times) {
            let x = instantOf(ymd, t, timeZone);
            while (x < prev)
                x += 24 * 3600_000;
            at.push(x);
            prev = x;
        }
        fixedIqama = row.iqama ?? [];
        source = 'table';
    }
    else {
        at = calcDay(spec, ymd, hijriAdjust);
    }
    if (!at)
        return null;
    const iqama = PRAYER_KEYS.map((k, i) => {
        if (k === 'sunrise')
            return null;
        const base = at[i];
        const fromTable = fixedIqama[i];
        const sp = fromTable && IQAMA_RE.test(fromTable) ? fromTable : (spec.iqama?.[k] ?? '');
        return iqamaAt(sp, base, ymd, timeZone);
    });
    return { date: ymd, at, iqama, source };
}
/** Iqama-Zeitpunkt aus "+N" oder "HH:mm"; eine feste Zeit vor dem Gebetsbeginn gilt nicht (dann Gebetsbeginn). */
export function iqamaAt(spec, base, ymd, timeZone) {
    if (!spec || !IQAMA_RE.test(spec))
        return null;
    if (spec.startsWith('+'))
        return base + Number(spec.slice(1)) * 60_000;
    let x = instantOf(ymd, spec, timeZone);
    // Feste Zeit nach Mitternacht (z. B. Isha-Iqama 00:15 im Sommer)
    if (x < base - 12 * 3600_000)
        x += 24 * 3600_000;
    return Math.max(x, base);
}
const dayIndex = new WeakMap();
function findDay(t, ymd) {
    let idx = dayIndex.get(t);
    if (!idx) {
        idx = new Map(t.days.map((d) => [d.date, d]));
        dayIndex.set(t, idx);
    }
    return idx.get(ymd);
}
/** Jumu'ah-Zeiten "13:15, 14:30" → ["13:15", "14:30"] (zweistellig). */
export function jumuahTimes(v) {
    if (!v || !JUMUAH_RE.test(v))
        return [];
    return v.split(',').map((s) => s.trim()).filter(Boolean).map((s) => s.padStart(5, '0'));
}
function events(spec, d, tz) {
    const out = [];
    const jum = weekdayOf(d.date) === 5 ? jumuahTimes(spec.jumuah) : [];
    PRAYER_KEYS.forEach((key, i) => {
        out.push({ key, at: d.at[i], kind: 'adhan' });
        // Freitag mit Jumu'ah-Zeiten: diese ersetzen die Dhuhr-Iqama
        if (key === 'dhuhr' && jum.length) {
            for (const j of jum)
                out.push({ key, at: instantOf(d.date, j, tz), kind: 'jumuah' });
            return;
        }
        const iq = d.iqama[i];
        if (iq != null)
            out.push({ key, at: iq, kind: 'iqama' });
    });
    return out;
}
/** Zustand des Bausteins zu einem Zeitpunkt. null, wenn für heute keine Zeiten vorliegen. */
export function prayerState(spec, now, o = {}) {
    const tz = o.timeZone;
    const cache = o.cache ?? new Map();
    const get = (ymd) => {
        if (!cache.has(ymd)) {
            if (cache.size > 8)
                cache.clear();
            cache.set(ymd, dayTimes(spec, ymd, tz, o.tables, o.hijriAdjust ?? 0));
        }
        return cache.get(ymd) ?? null;
    };
    const date = ymdAt(now, tz);
    const today = get(date);
    if (!today)
        return null;
    const prev = get(addDays(date, -1));
    const next = get(addDays(date, 1));
    const all = [prev, today, next].filter((d) => !!d).flatMap((d) => events(spec, d, tz)).sort((a, b) => a.at - b.at);
    // Aktuelles Gebet: letzter Gebetsbeginn ≤ jetzt (Sonnenaufgang beendet Fajr)
    let current = null;
    for (const e of all)
        if (e.kind === 'adhan' && e.at <= now)
            current = e.key === 'sunrise' ? null : e.key;
    const nextEv = all.find((e) => e.at > now) ?? null;
    // Hinweis: ab Iqama (ohne Iqama: ab Gebetsbeginn) für noticeMinutes Minuten
    const win = Math.max(1, Math.min(60, Number(spec.noticeMinutes) || 10)) * 60_000;
    let notice = false;
    for (const d of [prev, today]) {
        if (!d)
            continue;
        const ev = events(spec, d, tz);
        for (const k of IQAMA_KEYS) {
            // Beginn des Gebets in der Moschee: Jumu'ah bzw. Iqama, ohne beides der Gebetsbeginn
            const own = ev.filter((e) => e.key === k && e.kind !== 'adhan');
            const starts = own.length ? own.map((e) => e.at) : ev.filter((e) => e.key === k).map((e) => e.at);
            if (starts.some((s) => now >= s && now < s + win))
                notice = true;
        }
    }
    const friday = weekdayOf(date) === 5;
    return { date, day: today, current, next: nextEv, notice, friday, jumuah: friday ? jumuahTimes(spec.jumuah) : [] };
}
/** Countdown "1:05:09" bzw. "05:09". */
export function formatCountdown(ms) {
    const s = Math.max(0, Math.ceil(ms / 1000));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const mm = String(m).padStart(2, '0');
    const ss = String(sec).padStart(2, '0');
    return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
// ---------- Texte ----------
export const PRAYER_LOCALES = { de: 'de-DE', en: 'en-GB', tr: 'tr-TR', bs: 'bs-BA', ar: 'ar' };
export const PRAYER_TEXTS = {
    de: {
        names: { fajr: 'Fajr', sunrise: 'Sonnenaufgang', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha', jumuah: 'Jumu‘ah' },
        iqama: 'Iqama', next: (p) => `${p} in`, nextIqama: (p) => `Iqama ${p} in`, nextJumuah: 'Jumu‘ah in',
        notice: 'Bitte Handy lautlos schalten', noTimes: 'Keine Gebetszeiten für heute',
    },
    en: {
        names: { fajr: 'Fajr', sunrise: 'Sunrise', dhuhr: 'Dhuhr', asr: 'Asr', maghrib: 'Maghrib', isha: 'Isha', jumuah: 'Jumu‘ah' },
        iqama: 'Iqama', next: (p) => `${p} in`, nextIqama: (p) => `${p} iqama in`, nextJumuah: 'Jumu‘ah in',
        notice: 'Please switch your phone to silent', noTimes: 'No prayer times for today',
    },
    tr: {
        names: { fajr: 'İmsak', sunrise: 'Güneş', dhuhr: 'Öğle', asr: 'İkindi', maghrib: 'Akşam', isha: 'Yatsı', jumuah: 'Cuma' },
        iqama: 'Kamet', next: (p) => `${p} vaktine kalan`, nextIqama: (p) => `${p} kametine kalan`, nextJumuah: 'Cuma namazına kalan',
        notice: 'Lütfen telefonunuzu sessize alın', noTimes: 'Bugün için namaz vakti yok',
    },
    bs: {
        names: { fajr: 'Zora', sunrise: 'Izlazak sunca', dhuhr: 'Podne', asr: 'Ikindija', maghrib: 'Akšam', isha: 'Jacija', jumuah: 'Džuma' },
        iqama: 'Ikamet', next: (p) => `${p} za`, nextIqama: (p) => `Ikamet (${p}) za`, nextJumuah: 'Džuma za',
        notice: 'Molimo, utišajte mobitel', noTimes: 'Nema vaktova za danas',
    },
    ar: {
        names: { fajr: 'الفجر', sunrise: 'الشروق', dhuhr: 'الظهر', asr: 'العصر', maghrib: 'المغرب', isha: 'العشاء', jumuah: 'الجمعة' },
        iqama: 'الإقامة', next: (p) => `المتبقي على ${p}`, nextIqama: (p) => `المتبقي على إقامة ${p}`, nextJumuah: 'المتبقي على صلاة الجمعة',
        notice: 'يرجى إغلاق الهاتف أو وضعه على الصامت', noTimes: 'لا توجد مواقيت لهذا اليوم',
    },
};
export const isPrayerLang = (v) => typeof v === 'string' && PRAYER_LANGS.includes(v);
/**
 * Gebietsschema, das die Laufzeit wirklich kennt: Bildschirme (Chromium-Varianten, WebViews) haben teils reduzierte
 * ICU-Daten ohne Bosnisch. Dann null (Bosnisch: eigene Namen, sonst Englisch).
 */
const supported = new Map();
function intlLocale(lang) {
    if (!supported.has(lang)) {
        // Bosnisch nicht durch Kroatisch ersetzen (andere Monatsnamen: listopad statt oktobar)
        const tries = lang === 'bs' ? ['bs-BA', 'bs'] : [PRAYER_LOCALES[lang]];
        let hit = null;
        try {
            // Manche Chromium-Builds melden „bs" als unterstützt, formatieren aber ohne Daten („2026 M10 20"): Monatsnamen prüfen.
            // „oktobar“, nicht nur „okt“: sonst gilt ein auf die Systemsprache zurückfallendes „Oktober“ (deutsches Windows) als Bosnisch.
            const real = (l) => lang !== 'bs'
                || /oktobar/i.test(new Intl.DateTimeFormat(l, { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 9, 15))));
            hit = tries.find((l) => Intl.DateTimeFormat.supportedLocalesOf([l]).length > 0 && real(l)) ?? null;
        }
        catch {
            hit = null;
        }
        supported.set(lang, hit);
    }
    return supported.get(lang);
}
/** Für Tests: erkannte Gebietsschemata vergessen. */
export function resetPrayerLocales() {
    supported.clear();
}
/** Bosnische Namen (Ausweichlösung ohne ICU-Daten). */
const BS_DAYS = ['nedjelja', 'ponedjeljak', 'utorak', 'srijeda', 'četvrtak', 'petak', 'subota'];
const BS_MONTHS = ['januar', 'februar', 'mart', 'april', 'maj', 'juni', 'juli', 'august', 'septembar', 'oktobar', 'novembar', 'decembar'];
const BS_HIJRI = ['muharrem', 'safer', 'rebiu-l-evvel', 'rebiu-l-ahir', 'džumade-l-ula', 'džumade-l-uhra', 'redžeb', 'ša\u2018ban', 'ramazan', 'ševval', 'zu-l-ka\u2018de', 'zu-l-hidždže'];
/** Gregorianisches Datum in der Sprache des Bausteins. */
export function gregorianText(ymd, lang) {
    const [y, m, d] = parseYmd(ymd);
    const date = new Date(Date.UTC(y, m - 1, d));
    const loc = intlLocale(lang);
    if (!loc && lang === 'bs')
        return `${BS_DAYS[date.getUTCDay()]}, ${d}. ${BS_MONTHS[m - 1]} ${y}.`;
    return new Intl.DateTimeFormat(loc ?? 'en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
}
/** Hijri-Datum (Umm-al-Qura-Kalender über Intl) mit Tageskorrektur −2 … +2. */
export function hijriText(ymd, lang, adjustDays = 0) {
    const [y, m, d] = parseYmd(ymd);
    const adj = Math.max(-2, Math.min(2, Math.round(Number(adjustDays) || 0)));
    const date = new Date(Date.UTC(y, m - 1, d + adj));
    const loc = intlLocale(lang);
    try {
        if (!loc && lang === 'bs') {
            const parts = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', { day: 'numeric', month: 'numeric', year: 'numeric', timeZone: 'UTC' }).formatToParts(date);
            const part = (t) => Number(parts.find((p) => p.type === t)?.value.replace(/\D/g, ''));
            const hm = part('month');
            return hm >= 1 && hm <= 12 ? `${part('day')}. ${BS_HIJRI[hm - 1]} ${part('year')}. h.` : '';
        }
        return new Intl.DateTimeFormat(`${loc ?? 'en-GB'}-u-ca-islamic-umalqura`, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
    }
    catch {
        return '';
    }
}
//# sourceMappingURL=prayer.js.map